import React, { useRef, useState } from 'react';
import { FaFileAlt, FaTrash, FaDownload, FaUpload } from 'react-icons/fa';
import { SectionCard, palette } from './ui';
import { downloadResume } from '../../services/profileService';

const ACCEPTED = ['.pdf', '.doc', '.docx'];
const MAX_BYTES = 5 * 1024 * 1024;

export default function ResumeCard({ isDarkMode, profile, resume }) {
  const c = palette(isDarkMode);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [localError, setLocalError] = useState(null);

  const pickFile = () => inputRef.current && inputRef.current.click();

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setLocalError(null);
    const ext = `.${file.name.split('.').pop().toLowerCase()}`;
    if (!ACCEPTED.includes(ext)) {
      setLocalError('Please upload a PDF or Word file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError('File is too large. Maximum size is 5 MB.');
      return;
    }
    setUploading(true);
    await resume.upload(file);
    setUploading(false);
  };

  const openResume = async () => {
    setDownloading(true);
    try {
      await downloadResume(profile.resume_filename);
    } catch {
      setLocalError('Could not download resume. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <SectionCard
      id="resume"
      title="Resume"
      optional
      subtitle="Not required — your profile works as your resume"
      isDarkMode={isDarkMode}
    >
      <input ref={inputRef} type="file" accept={ACCEPTED.join(',')} onChange={onFile} className="hidden" />
      {profile.resume_url ? (
        <div className="flex flex-wrap items-center gap-3">
          <FaFileAlt className={`h-5 w-5 ${c.secondaryText}`} />
          <div className="flex-1 min-w-[160px]">
            <p className={`text-sm font-medium ${c.text}`}>{profile.resume_filename || 'Resume'}</p>
            {profile.resume_updated_at && (
              <p className={`text-xs ${c.secondaryText}`}>
                Updated {new Date(profile.resume_updated_at).toLocaleDateString('en-IN')}
              </p>
            )}
          </div>
          <button
            onClick={openResume}
            disabled={downloading}
            className={`p-2 rounded-full ${c.hover} ${c.secondaryText}`}
            aria-label="View resume"
          >
            <FaDownload className="h-4 w-4" />
          </button>
          <button onClick={pickFile} disabled={uploading} className={`px-4 py-2 rounded-lg text-sm font-medium ${c.secondaryBtn}`}>
            {uploading ? 'Uploading…' : 'Replace'}
          </button>
          <button onClick={resume.remove} aria-label="Delete resume" className={`p-2 rounded-full ${c.hover} text-red-500`}>
            <FaTrash className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={pickFile}
          disabled={uploading}
          className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium border border-dashed w-full justify-center ${c.hover} ${c.secondaryText} ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
        >
          <FaUpload className="h-4 w-4" />
          {uploading ? 'Uploading…' : 'Upload resume (PDF or Word, max 5 MB)'}
        </button>
      )}
      {localError && <p className="text-sm text-red-500 mt-2">{localError}</p>}
    </SectionCard>
  );
}
